import { reactive } from "vue";
import { api, authApi, getToken, setToken, onTokenChange } from "./api";

export const session = reactive({
  token: getToken(),
  user: null,
  developer: null,
  memberRole: null,
  totpEnabled: false,
  signupMode: null,
  ready: false
});

onTokenChange((token) => {
  session.token = token;
  if (!token) clearIdentity();
});

function clearIdentity() {
  session.user = null;
  session.developer = null;
  session.memberRole = null;
  session.totpEnabled = false;
}

function applyMe(me) {
  session.user = me?.user || null;
  session.developer = me?.developer || null;
  session.memberRole = me?.member_role || null;
  session.totpEnabled = !!me?.totp_enabled;
  if (me?.signup_mode) session.signupMode = me.signup_mode;
}

export async function refreshMe() {
  applyMe(await api.me());
}

export async function restore() {
  if (!session.token) {
    session.ready = true;
    return;
  }
  try {
    await refreshMe();
  } catch (e) {
    // An expired session is the normal case here; a network failure keeps the token.
    if (e.status === 401 || e.status === 403) setToken(null);
  } finally {
    session.ready = true;
  }
}

// Outcome of a password sign-in: "signed_in" | "totp" | "verify_email".
export async function signInWithPassword(email, password) {
  try {
    const result = await authApi.signInEmail(email, password);
    if (result?.twoFactorRedirect) return "totp";
    adoptBodyToken(result);
    await refreshMe();
    return "signed_in";
  } catch (e) {
    if (e.code === "EMAIL_NOT_VERIFIED") return "verify_email";
    throw e;
  }
}

export async function completeTwoFactor(code, { backup = false, trustDevice = false } = {}) {
  const result = backup ? await authApi.verifyBackupCode(code) : await authApi.verifyTotp(code, trustDevice);
  adoptBodyToken(result);
  await refreshMe();
}

// Returns true when the hub signed the user in as part of verification.
export async function verifyEmailOtp(email, otp) {
  const result = await authApi.verifyEmail(email, otp);
  adoptBodyToken(result);
  if (!getToken()) return false;
  await refreshMe();
  return true;
}

// The header is authoritative; the body token is a fallback when a proxy strips it.
function adoptBodyToken(result) {
  if (!getToken() && result && typeof result.token === "string" && result.token) setToken(result.token);
}

export async function signOut() {
  try {
    if (session.token) await authApi.signOut();
  } catch (e) {
    // Cleared locally regardless: a network failure must not leave the tab signed in.
  }
  setToken(null);
}

export const canManageTeam = () => ["owner", "admin"].includes(session.memberRole);
